/* eslint-disable class-methods-use-this */
/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc. The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Service for generating assignment titles using Berlin API
 * @author Satyaki Bose
 */

import Framework, { BaseService } from '@greenville/framework';
import {
  AET_STORE_EVENTS, ASSIGNMENT_TITLE_CONFIG,
  DEFAULT_MAX_ASSIGNMENT_TITLE_LENGTH
} from '../common/Constants';
import env from '../../../common/env';
import { getContainerContextValue } from '../utils/AssignmentUtils';

/**
 * Service for generating assignment titles using Berlin API
 * @author Satyaki Bose
 */
export default class AssignmentTitleService extends BaseService {
  constructor() {
    super(AET_STORE_EVENTS.ASSIGNMENT_TITLE_REQUESTED, 'assignmentTitleStatus');
  }

  /**
   * Generate assignment titles using Berlin API
   * @param {string} eventName - Event name
   * @param {Object} eventData - Event data containing contextId, titles, excludeTitles
   * @returns {Promise} - API response promise
   */
  handleEvent(eventName, requestPayload = {}) {
    const {
      onComplete, graphId, graphVersion, ...payLoad
    } = requestPayload;
    const headerParams = {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'x-berlin-tenant-id': env.BERLIN.TENANT_ID,
        'x-berlin-tenant-key': env.BERLIN.TENANT_KEY
      }
    };

    return this.getClient('berlin').post(
      `/1.0/run-graph/${graphId}/versions/${graphVersion}`,
      payLoad, headerParams
    );
  }

  /**
   * Handle successful response
   * @param {Object} response - API response
   */
  handleResponse(response, eventData = {}) {
    const { onComplete = () => {}, onError = () => {} } = eventData;
    const { data } = response;
    const { output } = data || {};
    const syllabusStore = Framework.getStoreRegistry().getStore('syllabus');
    try {
      const parsedOutput = JSON.parse(output);
      const titles = parsedOutput?.titles || [];
      // Truncate titles if they exceed max length
      // Get max length from marquee config
      const maxLength = getContainerContextValue(ASSIGNMENT_TITLE_CONFIG.MAX_LENGTH_KEY, DEFAULT_MAX_ASSIGNMENT_TITLE_LENGTH);
      const truncatedTitles = titles.map((item) => {
        if (item?.title && item.title.length > maxLength) {
          return {
            ...item,
            title: item.title.substring(0, maxLength)
          };
        }
        return item;
      });
      syllabusStore.onTitleGenerationCompleted(truncatedTitles);
      onComplete();
      Framework.getEventManager().publish(AET_STORE_EVENTS.ASSIGNMENT_TITLE_COMPLETED);
    } catch (error) {
      console.error('Error parsing output:', error);
      onError(error);
    }
  }

  /**
   * Handle error response
   * @param {Object} error - Error object
   */
  handleError(error, eventData = {}) {
    const { onError = () => {} } = eventData;
    onError(error);
    Framework.getEventManager().publish(AET_STORE_EVENTS.ASSIGNMENT_TITLE_FAILED, error);
  }
}
